import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck, Package, History, LogOut, Plus, Eye, User, Phone,
  ArrowRight, RotateCcw, X, Search, Calendar, MapPin, Box, CreditCard
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { logout } from '../firebase/auth';

// --- STYLED COMPONENTS ---

const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: #f8fafc; /* Slate-50 */
  padding-top: 80px; /* Header Offset */
  display: flex;
`;

const Sidebar = styled.aside`
  width: 280px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 32px 24px;
  position: fixed;
  top: 80px;
  bottom: 0;
  left: 0;
  overflow-y: auto;
  z-index: 10;

  @media (max-width: 1024px) {
    display: none; /* Hide on mobile/tablet for now, or implement drawer */
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 32px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 32px;

  .avatar {
    width: 48px;
    height: 48px;
    background: #e0e7ff;
    color: #4f46e5;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }

  .info {
    h3 { font-size: 1rem; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
    p { font-size: 0.85rem; color: #64748b; }
  }
`;

const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: ${props => props.$active ? '#eff6ff' : 'transparent'};
  color: ${props => props.$active ? '#2563eb' : '#64748b'};
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  svg { width: 20px; height: 20px; }
`;

const LogoutBtn = styled(NavItem)`
  margin-top: auto;
  color: #ef4444;
  &:hover { background: #fef2f2; color: #dc2626; }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 280px; /* Offset for sidebar */
  padding: 40px;
  max-width: 1600px;

  @media (max-width: 1024px) {
    margin-left: 0;
    padding: 24px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 40px;
  h1 { font-size: 2rem; font-weight: 800; color: #0f172a; margin-bottom: 8px; }
  p { color: #64748b; font-size: 1.1rem; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 48px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  .content {
    .label { font-size: 0.9rem; color: #64748b; margin-bottom: 8px; font-weight: 500; }
    .value { font-size: 2rem; font-weight: 700; color: #0f172a; }
  }

  .icon {
    padding: 12px;
    border-radius: 12px;
    background: ${props => props.$bg || '#f1f5f9'};
    color: ${props => props.$color || '#64748b'};
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const OrdersGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const OrderCard = styled(motion.div)`
  background: white;
  padding: 20px 24px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr 1fr;
  align-items: center;
  gap: 24px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #6366f1;
    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .id-col {
    h4 { font-size: 1.1rem; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
    span { font-size: 0.85rem; color: #64748b; }
  }

  .info-col {
    font-size: 0.95rem;
    color: #475569;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .action-col {
    display: flex;
    justify-content: flex-end;
    @media (max-width: 768px) { justify-content: flex-start; }
  }
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;

  ${props => {
    switch (props.$status?.toLowerCase()) {
      case 'delivered': return `background: #dcfce7; color: #166534;`;
      case 'in_transit': return `background: #dbeafe; color: #1e40af;`;
      case 'out_for_delivery': return `background: #fef9c3; color: #854d0e;`;
      case 'return': return `background: #fee2e2; color: #991b1b;`;
      default: return `background: #f1f5f9; color: #475569;`;
    }
  }}
`;

const Button = styled.button`
  padding: 10px 20px;
  background: ${props => props.$primary ? '#4f46e5' : 'white'};
  color: ${props => props.$primary ? 'white' : '#475569'};
  border: 1px solid ${props => props.$primary ? '#4f46e5' : '#e2e8f0'};
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$primary ? '#4338ca' : '#f8fafc'};
  }
`;

/* --- MODAL STYLES --- */
const ModalOverlay = styled(motion.div)`
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px);
  z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white; width: 100%; max-width: 800px; max-height: 90vh;
  border-radius: 24px; overflow-y: auto; position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  padding: 24px 32px; border-bottom: 1px solid #e2e8f0;
  display: flex; justify-content: space-between; align-items: center;
  background: #f8fafc;
  
  h2 { font-size: 1.5rem; font-weight: 700; color: #0f172a; }
`;

const CloseButton = styled.button`
  background: white; border: 1px solid #e2e8f0; padding: 8px; border-radius: 50%;
  cursor: pointer; color: #64748b; transition: all 0.2s;
  &:hover { background: #fee2e2; color: #ef4444; border-color: #fee2e2; }
`;

const ModalBody = styled.div`
  padding: 32px;
`;

const DetailGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 24px; margin-bottom: 32px;
`;

const DetailItem = styled.div`
  .label { font-size: 0.85rem; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 6px; letter-spacing: 0.05em; }
  .value { font-size: 1rem; color: #0f172a; font-weight: 500; }
`;

const SectionHeader = styled.h3`
  font-size: 1.1rem; font-weight: 700; color: #0f172a; margin-bottom: 16px; 
  padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;
`;

// --- COMPONENT ---

const Dashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [backendShipments, setBackendShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (user) fetchOrders();
  }, [user]);

  const handleLogout = async () => {
    try { await logout(); navigate('/'); } catch (e) { console.error(e); }
  };

  const fetchOrders = async () => {
    try {
      if (user?.phoneNumber) {
        const cleanPhone = user.phoneNumber.replace(/^\+91/, '');
        const res = await fetch(`https://api.sddlogistics.com/api/shipments/search-by-seller-phone/${cleanPhone}`);
        const data = await res.json();

        if (data.success && data.data) {
          setBackendShipments(data.data);
          const formatted = data.data.map(s => ({
            id: s.shipment_id,
            trackingNumber: s.shipment_id,
            status: s.status,
            sender: s.sellerDetails?.seller?.sellerName || 'N/A',
            recipient: s.recipientDetails?.customerName || 'N/A',
            date: new Date(s.createdAt).toLocaleDateString(),
            cost: s.chargesAndPayments?.totalShipmentCost || 0
          }));
          setOrders(formatted);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const stats = (() => {
    const total = backendShipments.length;
    return {
      total,
      delivered: backendShipments.filter(s => s.status === 'delivered').length,
      transit: backendShipments.filter(s => s.status === 'in_transit').length,
      pending: backendShipments.filter(s => s.status === 'created').length,
      returned: backendShipments.filter(s => s.status === 'return').length
    };
  })();

  const handleOrderClick = (order) => {
    const fullData = backendShipments.find(s => s.shipment_id === order.trackingNumber);
    setSelectedOrder(fullData || order);
  };

  if (loading) return <DashboardContainer><div style={{ padding: '40px' }}>Loading...</div></DashboardContainer>;

  return (
    <DashboardContainer>
      {/* SIDEBAR */}
      <Sidebar>
        <UserProfile>
          <div className="avatar"><User size={24} /></div>
          <div className="info">
            <h3>{userProfile?.displayName || 'User'}</h3>
            <p>{user?.phoneNumber}</p>
          </div>
        </UserProfile>

        <NavList>
          <NavItem $active={activeSection === 'overview'} onClick={() => setActiveSection('overview')}>
            <Truck /> Overview
          </NavItem>
          <NavItem $active={activeSection === 'orders'} onClick={() => setActiveSection('orders')}>
            <Package /> Active Orders
          </NavItem>
          <NavItem $active={activeSection === 'history'} onClick={() => setActiveSection('history')}>
            <History /> Order History
          </NavItem>

          <div style={{ marginTop: 'auto' }}>
            <LogoutBtn onClick={handleLogout}>
              <LogOut /> Logout
            </LogoutBtn>
          </div>
        </NavList>
      </Sidebar>

      {/* MAIN CONTENT */}
      <MainContent>
        <PageHeader>
          <h1>Dashboard</h1>
          <p>Welcome back, here is what's happening with your shipments.</p>
        </PageHeader>

        {activeSection === 'overview' && (
          <>
            <StatsGrid>
              <StatCard $bg="#eef2ff" $color="#4f46e5">
                <div className="content">
                  <div className="label">Total Orders</div>
                  <div className="value">{stats.total}</div>
                </div>
                <div className="icon"><Box size={24} /></div>
              </StatCard>
              <StatCard $bg="#dbeafe" $color="#2563eb">
                <div className="content">
                  <div className="label">In Transit</div>
                  <div className="value">{stats.transit}</div>
                </div>
                <div className="icon"><Truck size={24} /></div>
              </StatCard>
              <StatCard $bg="#dcfce7" $color="#166534">
                <div className="content">
                  <div className="label">Delivered</div>
                  <div className="value">{stats.delivered}</div>
                </div>
                <div className="icon"><Package size={24} /></div>
              </StatCard>
              <StatCard $bg="#fee2e2" $color="#991b1b">
                <div className="content">
                  <div className="label">Returned</div>
                  <div className="value">{stats.returned}</div>
                </div>
                <div className="icon"><RotateCcw size={24} /></div>
              </StatCard>
            </StatsGrid>

            <SectionTitle>Recent Orders</SectionTitle>
          </>
        )}

        <OrdersGrid>
          {(activeSection === 'orders' || activeSection === 'overview' ? orders : orders.filter(o => o.status === 'delivered')).map(order => (
            <OrderCard key={order.id} onClick={() => handleOrderClick(order)} whileHover={{ scale: 1.01 }}>
              <div className="id-col">
                <h4>{order.trackingNumber}</h4>
                <span>{order.date}</span>
              </div>
              <div className="info-col">
                <User size={16} /> {order.recipient}
              </div>
              <div className="info-col">
                <StatusBadge $status={order.status}>
                  {order.status === 'delivered' ? <Package size={14} /> : <Truck size={14} />}
                  {order.status}
                </StatusBadge>
              </div>
              <div className="action-col">
                <Button $primary onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/tracking?trackingNumber=${order.trackingNumber}`);
                }}>
                  <Eye size={16} /> Track
                </Button>
              </div>
            </OrderCard>
          ))}
          {orders.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <Box size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <h3>No orders found</h3>
            </div>
          )}
        </OrdersGrid>
      </MainContent>

      {/* ORDER DETAIL MODAL */}
      <AnimatePresence>
        {selectedOrder && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedOrder(null)}>
            <ModalContent initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <h2>Order #{selectedOrder.shipment_id}</h2>
                <CloseButton onClick={() => setSelectedOrder(null)}><X size={20} /></CloseButton>
              </ModalHeader>

              <ModalBody>
                <div style={{ marginBottom: '32px', display: 'flex', gap: '12px' }}>
                  <StatusBadge $status={selectedOrder.status} style={{ fontSize: '1rem', padding: '8px 16px' }}>
                    {selectedOrder.status}
                  </StatusBadge>
                </div>

                <SectionHeader>Shipment Details</SectionHeader>
                <DetailGrid>
                  <DetailItem><div className="label">Created Date</div><div className="value">{new Date(selectedOrder.createdAt).toLocaleDateString()}</div></DetailItem>
                  <DetailItem><div className="label">Payment Mode</div><div className="value">{selectedOrder.paymentMethod}</div></DetailItem>
                  <DetailItem><div className="label">Total Cost</div><div className="value" style={{ color: '#166534' }}>₹{selectedOrder.chargesAndPayments?.totalShipmentCost}</div></DetailItem>
                </DetailGrid>

                <SectionHeader>Addresses</SectionHeader>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px' }}>
                  <div>
                    <h4 style={{ marginBottom: '12px', color: '#64748b' }}>Sender</h4>
                    <p style={{ fontWeight: '600' }}>{selectedOrder.sellerDetails?.sellerName}</p>
                    <p>{selectedOrder.sellerDetails?.sellerPhone}</p>
                  </div>
                  <div>
                    <h4 style={{ marginBottom: '12px', color: '#64748b' }}>Recipient</h4>
                    <p style={{ fontWeight: '600' }}>{selectedOrder.recipientDetails?.customerName}</p>
                    <p>{selectedOrder.recipientDetails?.address?.city}, {selectedOrder.recipientDetails?.address?.pincode}</p>
                    <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px' }}>
                      {selectedOrder.recipientDetails?.address?.addressLine1}
                    </p>
                  </div>
                </div>

                {selectedOrder.history && (
                  <>
                    <SectionHeader>History</SectionHeader>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {selectedOrder.history.map((h, i) => (
                        <div key={i} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                          <div style={{ minWidth: '140px', fontSize: '0.9rem', color: '#64748b' }}>
                            {new Date(h.timestamp).toLocaleString()}
                          </div>
                          <StatusBadge $status={h.status} style={{ fontSize: '0.8rem' }}>{h.status}</StatusBadge>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </DashboardContainer>
  );
};

export default Dashboard;